import React, { useState } from "react";
import axios from "axios";
import { Tag, X, Check, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Promo code field reusable on booking & event payment screens.
 *
 * Props:
 *   - subtotal (number, required) — current amount in € BEFORE discount
 *   - surface (string, default "shop") — "shop" | "booking" | "events"
 *   - onApplied(promo) — called with { code, discount_amount, discount_type, value } once validated
 *   - onCleared() — called when user removes the applied promo
 *   - applied (object|null) — the currently applied promo (controlled by parent)
 *   - testIdPrefix (string) — disambiguates data-testids when multiple instances on the same page
 */
export default function PromoCodeInput({
  subtotal,
  surface = "shop",
  onApplied,
  onCleared,
  applied,
  testIdPrefix = "promo",
}) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleApply = async () => {
    setError("");
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      setError("Entre un code");
      return;
    }
    if (!subtotal || subtotal <= 0) {
      setError("Le montant est invalide");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/promo-codes/validate`,
        { code: trimmed, subtotal, surface },
        { withCredentials: true },
      );
      if (res.data?.valid) {
        onApplied({
          code: res.data.code,
          discount_amount: res.data.discount_amount,
          discount_type: res.data.discount_type,
          value: res.data.value,
        });
        setCode("");
      } else {
        setError(res.data?.error || "Code invalide");
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    setError("");
    setCode("");
    onCleared();
  };

  if (applied) {
    return (
      <div
        className="flex items-center justify-between gap-2 rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-3 py-2"
        data-testid={`${testIdPrefix}-applied`}
      >
        <div className="flex items-center gap-2 text-sm">
          <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
          <span className="text-emerald-300 font-mono font-medium">{applied.code}</span>
          <span className="text-xs text-emerald-300/70">
            −{Number(applied.discount_amount).toFixed(2)} €
          </span>
        </div>
        <button
          type="button"
          onClick={handleRemove}
          className="text-emerald-300/70 hover:text-white p-1 rounded transition-colors"
          aria-label="Retirer le code promo"
          data-testid={`${testIdPrefix}-remove`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div data-testid={`${testIdPrefix}-input`}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B59ED1]" />
          <Input
            value={code}
            onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleApply(); } }}
            placeholder="Code promo"
            disabled={loading}
            className="pl-9 bg-[#1B132B] border-white/15 text-white placeholder:text-[#B59ED1]/60 uppercase tracking-wider"
            data-testid={`${testIdPrefix}-field`}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleApply}
          disabled={loading || !code.trim()}
          className="border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10"
          data-testid={`${testIdPrefix}-apply`}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Appliquer"}
        </Button>
      </div>
      {error && (
        <p className="text-xs text-red-400 mt-1.5" data-testid={`${testIdPrefix}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
